import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { 
  HomeIcon, 
  UserGroupIcon, 
  SparklesIcon, 
  UserIcon,
  LightBulbIcon,
  Squares2X2Icon,
  RocketLaunchIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  SignalIcon
} from '@heroicons/react/24/outline';
import { AuthContext } from '../contexts/AuthContext';

const AppFooter: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const authContext = useContext(AuthContext);
  const isAuthenticated = !!authContext?.user;

  // Different quick links based on authentication status
  const getQuickLinks = () => {
    if (isAuthenticated) {
      return [
        { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
        { name: 'My Teams', href: '/teams', icon: UserGroupIcon },
        { name: 'Profile', href: '/profile', icon: UserIcon },
      ];
    } else {
      return [
        { name: 'Sign In', href: '/auth', icon: UserIcon },
        { name: 'Get Started', href: '/auth', icon: RocketLaunchIcon },
      ];
    }
  };

  const quickLinks = getQuickLinks();

  const features = [
    { name: 'Brainstorming Boards', icon: LightBulbIcon },
    { name: 'Kanban Boards', icon: Squares2X2Icon },
    { name: 'Mood Boards', icon: SparklesIcon },
    { name: 'Design Sprints', icon: RocketLaunchIcon },
    { name: 'Real-time Collaboration', icon: UserGroupIcon },
  ];

  const supportLinks = [
    { name: 'Contact Us', href: '/contact', icon: EnvelopeIcon },
    { name: 'Privacy Policy', href: '/privacy-policy', icon: ShieldCheckIcon },
    { name: 'Terms of Service', href: '/terms-of-service', icon: DocumentTextIcon },
    { name: 'Accessibility', href: '/accessibility', icon: QuestionMarkCircleIcon },
    { name: 'Help & Support', href: '/help-support', icon: QuestionMarkCircleIcon },
    { name: 'Status Page', href: '/status', icon: SignalIcon },
  ];

  return (
    <footer className="bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                <SparklesIcon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                StyleSync
              </h3>
            </div>
            <p className="text-gray-600 text-sm mb-4 leading-relaxed">
              Where Creative Teams Meet Visually. Unify brainstorming, organizing, and presenting ideas in one collaborative workspace.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://github.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                aria-label="GitHub"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
              {isAuthenticated ? 'Quick Links' : 'Navigation'}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="flex items-center text-gray-600 hover:text-blue-600 transition-colors duration-200 text-sm group"
                  >
                    <link.icon className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform duration-200" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Features */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
              Features
            </h4>
            <ul className="space-y-3">
              {features.map((feature) => (
                <li key={feature.name}>
                  <div className="flex items-center text-gray-600 text-sm">
                    <feature.icon className="w-4 h-4 mr-2 text-blue-500" />
                    {feature.name}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Support & Contact */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
              Support
            </h4>
            <ul className="space-y-3">
              {supportLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="flex items-center text-gray-600 hover:text-blue-600 transition-colors duration-200 text-sm group"
                  >
                    <link.icon className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform duration-200" />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span>© {currentYear} StyleSync. All rights reserved.</span>
              <span className="hidden sm:inline">•</span>
              <span className="hidden sm:inline">Made with ❤️ for creative teams</span>
            </div>
            <div className="flex items-center space-x-6 text-sm">
              <span className="text-gray-400">Version 1.0.0</span>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-green-600">All systems operational</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default AppFooter; 